/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ApplicationBubble.tsx - Burbuja Flotante de Aplicación Minimizada
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @description Globo flotante arrastrable para aplicaciones minimizadas
 * @version 1.0.0
 * @architecture WASP Frontend
 * 
 * FUNCIONALIDADES:
 * - ✅ Drag & drop
 * - ✅ Hover effects
 * - ✅ Restaurar/Cerrar
 * - ✅ Soporte dark/light theme
 * - ✅ Animaciones fluidas
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { WindowPosition } from '../hooks/useWindowManager';

interface ApplicationBubbleProps {
  id: string;
  appId: string;
  appName: string;
  appIcon: any;
  position: WindowPosition;
  zIndex: number;
  onMaximize: () => void;
  onClose: () => void;
  onDrag: (position: WindowPosition) => void;
  onBringToFront: () => void;
}

export const ApplicationBubble: React.FC<ApplicationBubbleProps> = ({
  id,
  appId,
  appName,
  appIcon,
  position,
  zIndex,
  onMaximize,
  onClose,
  onDrag,
  onBringToFront
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.bubble-close')) return;
    
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
    onBringToFront();
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const newX = Math.max(0, Math.min(window.innerWidth - 80, e.clientX - dragOffset.x));
        const newY = Math.max(0, Math.min(window.innerHeight - 80, e.clientY - dragOffset.y));
        
        onDrag({ x: newX, y: newY });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, position]);

  const bubbleStyle: React.CSSProperties = {
    position: 'fixed',
    left: position.x,
    top: position.y,
    width: '70px',
    height: '70px',
    zIndex,
    cursor: isDragging ? 'grabbing' : 'grab',
    transition: isDragging ? 'none' : 'all 0.3s ease',
    userSelect: 'none'
  };

  return (
    <div
      style={bubbleStyle}
      onMouseDown={handleMouseDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={(e) => {
        if (!isDragging && !(e.target as HTMLElement).closest('.bubble-close')) {
          onMaximize();
        }
      }}
    >
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          boxShadow: isHovered 
            ? '0 8px 24px rgba(0,0,0,0.3)' 
            : '0 4px 12px rgba(0,0,0,0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transform: isHovered ? 'scale(1.1)' : 'scale(1)',
          transition: 'all 0.3s ease',
          border: '3px solid var(--bs-body-bg)'
        }}
      >
        <FontAwesomeIcon 
          icon={appIcon} 
          style={{ 
            fontSize: '1.8rem', 
            color: '#fff',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
          }} 
        />

        {isHovered && (
          <>
            <div
              className="bubble-close"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              style={{
                position: 'absolute',
                top: '-5px',
                right: '-5px',
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: '#dc3545',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                animation: 'bubbleFadeIn 0.2s ease-out'
              }}
            >
              <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.7rem', color: '#fff' }} />
            </div>

            <div
              style={{
                position: 'absolute',
                bottom: '-35px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'var(--bs-dark)',
                color: '#fff',
                padding: '6px 12px',
                borderRadius: '6px',
                fontSize: '0.75rem',
                fontWeight: 500,
                whiteSpace: 'nowrap',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                pointerEvents: 'none',
                animation: 'bubbleFadeIn 0.2s ease-out',
                zIndex: 10000
              }}
            >
              {appName}
              <div
                style={{
                  position: 'absolute',
                  top: '-4px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '0',
                  height: '0',
                  borderLeft: '5px solid transparent',
                  borderRight: '5px solid transparent',
                  borderBottom: '5px solid var(--bs-dark)'
                }}
              />
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes bubbleFadeIn {
          from {
            opacity: 0;
            transform: translateY(-5px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes bubblePulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }
      `}</style>
    </div>
  );
};

export default ApplicationBubble;